import { useState } from 'react';
import { lookupAddresses, isAddressLookupConfigured, type AddressResult } from '../lib/addressLookup';

interface Props {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  /** Optional callback fired when an address is picked — receives lat/lng if available. */
  onPick?: (a: AddressResult) => void;
}

/**
 * Postcode-driven address picker.
 *
 * If the API key is configured, shows a "Find" button that fetches addresses
 * for the entered postcode. Otherwise falls back to a plain free-text input so
 * the form still works.
 */
export function AddressInput({ label = 'Address', value, onChange, onPick }: Props) {
  const configured = isAddressLookupConfigured();
  const [postcode, setPostcode] = useState('');
  const [results, setResults] = useState<AddressResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function search() {
    if (!postcode.trim()) return;
    setSearching(true);
    setError(null);
    setResults(null);
    try {
      const addrs = await lookupAddresses(postcode);
      setResults(addrs);
      if (addrs.length === 0) setError("Couldn't find that postcode — type the address below instead.");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSearching(false);
    }
  }

  function pick(a: AddressResult) {
    onChange(a.formatted);
    onPick?.(a);
    setResults(null);
    setPostcode('');
    setError(null);
  }

  return (
    <div>
      {configured ? (
        <div className="field" style={{ marginBottom: 6 }}>
          <label>Postcode lookup</label>
          <div className="postcode-row">
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value.toUpperCase())}
              placeholder="e.g. SW1A 1AA"
              autoCapitalize="characters"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={search}
              disabled={searching || !postcode.trim()}
            >
              {searching ? 'Looking…' : 'Find'}
            </button>
          </div>
        </div>
      ) : null}

      {error && <p style={{ color: 'var(--danger)', fontSize: 13, margin: '4px 0' }}>{error}</p>}

      {results && results.length > 0 && (
        <ul className="address-results">
          {results.map((a, i) => (
            <li key={i}>
              <button type="button" onClick={() => pick(a)}>{a.formatted}</button>
            </li>
          ))}
        </ul>
      )}

      <div className="field">
        <label>{label}</label>
        <textarea
          rows={2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={configured ? 'Pick from the list above or type freely' : 'Type the address'}
        />
      </div>
    </div>
  );
}
